import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, Tooltip } from '@mui/material';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import { BundleItem } from '~/components/Cruises/pages/Bundles/types';

import { useDeleteBundleItem } from '~/hooks/apis/cruises/useBundleItemMutations';

import BundleItemOptionList from './components/BundleItemOptionList';

const BUNDLE_ITEM_TYPE_LABELS: Record<string, string> = {
  PRE_ACCOMMODATION: 'Pre Accommodation',
  POST_ACCOMMODATION: 'Post Accommodation',
};

interface Props {
  bundleItem: BundleItem;
}

function BundlesItemListItem({ bundleItem }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { bundleId } = useParams<{ bundleId: string }>();
  const [expanded, setExpanded] = useState(true);

  const showConfirmDialog = useConfirmDialog();
  const deleteMutation = useDeleteBundleItem();

  const handleToggleExpand = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const handleDeleteRecord = useCallback(async () => {
    try {
      const confirmed = await showConfirmDialog({
        title: 'Delete Bundle Item?',
        description: 'This will delete the bundle item and all its options. Are you sure you want to proceed?',
      });

      if (!confirmed) {
        return;
      }

      await deleteMutation.mutateAsync({ bundleId, itemId: bundleItem.id });
      enqueueSnackbar('Bundle item deleted', { variant: 'success' });
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Error deleting bundle item';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  }, [enqueueSnackbar, bundleId, bundleItem.id, showConfirmDialog, deleteMutation]);

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mb: 1 }}>
      <ListItem
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <ListItemText
          primary={bundleItem.displayName}
          secondary={`Type: ${BUNDLE_ITEM_TYPE_LABELS[bundleItem.itemType] || bundleItem.itemType} | Max Price: $${
            bundleItem.maxPrice
          }`}
        />

        <Tooltip title={expanded ? 'Hide options' : 'Show options'}>
          <Button variant="text" size="small" onClick={handleToggleExpand}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
        </Tooltip>

        <Tooltip title="Delete bundle item">
          <Button
            variant="text"
            size="small"
            color="error"
            onClick={handleDeleteRecord}
            disabled={deleteMutation.isPending}
          >
            <DeleteIcon />
          </Button>
        </Tooltip>
      </ListItem>

      <Collapse in={expanded}>
        <Box pl={4} pr={2} pb={2}>
          <BundleItemOptionList bundleItemId={bundleItem.id} />
        </Box>
      </Collapse>
    </Box>
  );
}

export default BundlesItemListItem;
